import {
  OreButton,
  OreCardContainer,
  OreCardWrapper,
  OreDatePicker,
  OreInput,
  OreSelect,
  OreSelectPlaceholder,
  OreStack,
} from '@edenredespana/oreneta'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { invoicesTranslation } from '@/Invoices/ui/invoicesTranslation'
import {
  daysAfter,
  daysAgo,
  edenredProducts,
  getToday,
  isValidDate,
} from '@/Shared/domain'
import { edenredPremium } from '@/Shared/domain/enum/edenredPremium'
import { tableSearcherTranslations } from '@/Shared/ui/TableSearcher'
import { OreMultiSelect } from '@/Shared/ui/molecules/OreMultiSelect'
import { formTranslation } from '@/Shared/ui/sharedTranslation'
import { TransactionFiltersModel } from '@/Transactions/domain'
import { useTransaction } from '@/Transactions/ui/context/TransactionsContext'
import { UserType } from '@/User/domain/UserType'
import { useUserData } from '@/User/ui/context'

import { transactionsTranslation } from '../transactionsTranslation'

interface TransactionFilterProps {
  isNursery?: boolean
  handleSearch: (data: TransactionFiltersModel) => void
  handleClear: () => void
}

export const TransactionFilter = ({
  handleSearch,
  handleClear,
  isNursery,
}: TransactionFilterProps): JSX.Element => {
  const { t } = useTranslation()
  const { localsFilterData, filterValues } = useTransaction()
  const { userType } = useUserData()

  const filtersSchema = Yup.object().shape({
    beginDate: Yup.string().required(t(formTranslation.errors.fieldRequired)),
    endDate: Yup.string()
      .required(t(formTranslation.errors.fieldRequired))
      .test(
        'is-within-90-days',
        t(transactionsTranslation.table.filters.errors.dateWithin90days),
        function (value) {
          if (value > getToday()) return false
          if (!isNursery) {
            const { beginDate } = this.parent
            const isWithin = value <= daysAfter(beginDate, 90)
            return isWithin
          }
          return true
        },
      )
      .test(
        'is-major-than-begin-date',
        t(transactionsTranslation.table.filters.errors.endDateBeforeBeginDate),
        function (value) {
          const { beginDate } = this.parent
          const isMajor = value >= beginDate

          return isMajor
        },
      ),
    subProductCode: Yup.string(),
    productCode: Yup.string(),
    localCode: Yup.array().of(Yup.string()),
    childName: Yup.string(),
  })

  const { register, handleSubmit, formState, setValue, watch, clearErrors, trigger } =
    useForm<TransactionFiltersModel>({
      resolver: yupResolver(filtersSchema),
      mode: 'onChange',
      defaultValues: {
        productCode: '',
        subProductCode: '',
        childName: '',
        localCode: [],
      },
    })
  const { merchant } = useUserData()
  const isMasterGroup = merchant.isMasterGroup
  const isChild = merchant.isChild
  const isParent = merchant.isParent
  const isParentCommercial = merchant.isParentCommercial

  const watchEndDate = watch('endDate')
  const watchBeginDate = watch('beginDate')
  const watchProduct = watch('productCode')

  const YEAR = 365

  const clearFilters = (): void => {
    setValue('beginDate', '')
    setValue('endDate', '')
    setValue('subProductCode', '')
    setValue('productCode', '')
    setValue('childName', '')
    setValue('localCode', [])
    clearErrors('beginDate')
    clearErrors('endDate')
    handleClear()
  }

  useEffect(() => {
    if (!watchProduct) {
      setValue('subProductCode', '')
    }
  }, [watchBeginDate, watchEndDate, watchProduct])

  // Trigger endDate validation when beginDate is set
  useEffect(() => {
    if (watchBeginDate && watchEndDate) {
      trigger('endDate')
    }
  }, [watchBeginDate])

  useEffect(() => {
    if (isNursery) {
      const oneYearAgo = new Date()
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
      setValue('beginDate', oneYearAgo.toISOString().split('T')[0])
      setValue('endDate', new Date().toISOString().split('T')[0])
    }
  }, [])

  return (
    <OreCardContainer radius="top">
      <OreCardWrapper space="large">
        <OreStack placeContent="stretch">
          <OreStack placeContent="stretch">
            <OreCardContainer tone="neutral-100" hasShadow={false}>
              <OreCardWrapper>
                <OreStack space="large" placeContent="stretch">
                  <OreStack direction="row" placeContent="stretch" sameSize>
                    <OreDatePicker
                      label={t(transactionsTranslation.table.filters.beginDate)}
                      min={daysAgo(YEAR * 4)}
                      max={getToday()}
                      {...register('beginDate')}
                      required
                      hasError={!!formState.errors.beginDate}
                      errorMessage={
                        formState.errors.beginDate && formState.errors.beginDate?.message
                      }
                      onInput={() => {
                        if (isValidDate(watchEndDate)) {
                          trigger('endDate')
                        }
                      }}
                    />
                    <OreDatePicker
                      label={t(transactionsTranslation.table.filters.endDate)}
                      max={
                        watchBeginDate
                          ? isNursery
                            ? daysAfter(watchBeginDate, YEAR * 4)
                            : daysAfter(watchBeginDate, 90)
                          : getToday()
                      }
                      min={watchBeginDate ? watchBeginDate : daysAgo(YEAR * 4)}
                      {...register('endDate')}
                      required
                      hasError={!!formState.errors.endDate}
                      errorMessage={
                        formState.errors.endDate && formState.errors.endDate?.message
                      }
                      disabled={watchBeginDate ? false : true}
                    />
                  </OreStack>
                  {!isNursery ? (
                    <>
                      <OreStack direction="row" placeContent="stretch" sameSize>
                        <OreSelect
                          label={t(transactionsTranslation.table.filters.product.label)}
                          {...register('productCode')}
                          disabled={watchBeginDate && watchEndDate ? false : true}
                        >
                          <OreSelectPlaceholder
                            label={t(
                              transactionsTranslation.table.filters.product.placeholder,
                            )}
                          />
                          {filterValues?.map(product => (
                            <option
                              value={product.productId.toString()}
                              key={product.productId}
                            >
                              {product.productName}
                            </option>
                          ))}
                        </OreSelect>
                        <OreSelect
                          label={t(transactionsTranslation.table.filters.service.label)}
                          {...register('subProductCode')}
                          disabled={
                            watchBeginDate && watchEndDate && watchProduct ? false : true
                          }
                        >
                          <OreSelectPlaceholder
                            label={t(
                              transactionsTranslation.table.filters.service.placeholder,
                            )}
                          />
                          {filterValues
                            ?.filter(
                              product => product.productId.toString() === watchProduct,
                            )
                            .flatMap(product =>
                              product.subProducts.map(subProduct => (
                                <option
                                  value={subProduct.subProductId.toString()}
                                  key={subProduct.subProductId}
                                >
                                  {subProduct.subProductName}
                                </option>
                              )),
                            )}
                        </OreSelect>
                      </OreStack>
                      {((!isChild && isParent) ||
                        isParentCommercial ||
                        isMasterGroup) && (
                        <OreStack direction="row" placeContent="stretch" sameSize>
                          <OreMultiSelect
                            label={t(
                              userType === UserType.NURSERY
                                ? invoicesTranslation.table.header.nursery
                                : transactionsTranslation.table.filters.local.label,
                            )}
                            placeholder={t(
                              userType === UserType.NURSERY
                                ? transactionsTranslation.table.filters.product
                                    .placeholder
                                : transactionsTranslation.table.filters.local.placeholder,
                            )}
                            disabled={watchBeginDate && watchEndDate ? false : true}
                            searchPlaceholder={t(
                              userType === UserType.NURSERY
                                ? transactionsTranslation.table.filters.local
                                    .nurserySearchPlaceholder
                                : transactionsTranslation.table.filters.local.search,
                            )}
                            value={(watch('localCode') || []).filter(Boolean) as string[]}
                            maxVisibleTags={3}
                            options={(localsFilterData || []).map(local => {
                              const cif = (local as any).CIF ?? (local as any).cif
                              return {
                                value: local.code?.toString?.() || String(local.code),
                                label: local.name,
                                searchTokens: cif ? [String(cif)] : undefined,
                              }
                            })}
                            onChange={vals => {
                              setValue('localCode', vals, { shouldValidate: true })
                            }}
                          />
                        </OreStack>
                      )}
                    </>
                  ) : (
                    <>
                      <OreStack direction="row" placeContent="stretch" sameSize>
                        {((!isChild && isParent) ||
                          isParentCommercial ||
                          isMasterGroup) && (
                          <OreMultiSelect
                            label={t(
                              userType === UserType.NURSERY
                                ? invoicesTranslation.table.header.nursery
                                : transactionsTranslation.table.filters.local.label,
                            )}
                            placeholder={t(
                              userType === UserType.NURSERY
                                ? transactionsTranslation.table.filters.product
                                    .placeholder
                                : transactionsTranslation.table.filters.local.placeholder,
                            )}
                            disabled={watchBeginDate && watchEndDate ? false : true}
                            // Si no hay key específica de búsqueda en traducciones, usar fallback
                            searchPlaceholder={t(
                              userType === UserType.NURSERY
                                ? transactionsTranslation.table.filters.local
                                    .nurserySearchPlaceholder
                                : transactionsTranslation.table.filters.local.search,
                            )}
                            value={(watch('localCode') || []).filter(Boolean) as string[]}
                            maxVisibleTags={1}
                            options={(localsFilterData || []).map(local => {
                              const cif = (local as any).CIF ?? (local as any).cif // admite ambas convenciones
                              return {
                                value: local.code?.toString?.() || String(local.code),
                                label: local.name,
                                searchTokens: cif ? [String(cif)] : undefined,
                              }
                            })}
                            onChange={vals => {
                              setValue('localCode', vals, { shouldValidate: true })
                            }}
                          />
                        )}
                        <OreInput
                          {...register('childName')}
                          hasError={!!formState.errors.childName}
                          id="childName"
                          label={t(transactionsTranslation.guarderia.kidName)}
                          placeholder={t(
                            transactionsTranslation.guarderia.kidNamePlaceholder,
                          )}
                          maxLength={75}
                        />
                      </OreStack>
                    </>
                  )}
                </OreStack>
              </OreCardWrapper>
            </OreCardContainer>
            <OreStack direction="row">
              <OreButton
                category="primary"
                size="small"
                onClick={handleSubmit(handleSearch)}
              >
                {t(tableSearcherTranslations.search)}
              </OreButton>
              <OreButton category="secondary" size="small" onClick={clearFilters}>
                {t(tableSearcherTranslations.clear)}
              </OreButton>
            </OreStack>
          </OreStack>
        </OreStack>
      </OreCardWrapper>
    </OreCardContainer>
  )
}
